/**
 * AI Translation Service
 * Provides AI-powered translation between English and Chinese
 * Uses localStorage for caching translations with 24-hour TTL
 */

import { AIMonitoringService } from './aiMonitoringService';
import TranslationErrorHandler, { TranslationErrorType } from '../utils/translationErrorHandler';
import { supabase } from './supabaseClient';

// Supported languages (English and Chinese only)
type SupportedLanguage = 'en' | 'zh';

// Translation context types
type TranslationContext = 'survey' | 'task' | 'general';

// Translation result interface
export interface TranslationResult {
  originalText: string;
  translatedText: string;
  fromLanguage: SupportedLanguage;
  toLanguage: SupportedLanguage;
  cached: boolean;
  translatedAt: Date;
}

// Translation cache entry interface
interface TranslationCacheEntry {
  originalText: string;
  translatedText: string;
  fromLang: SupportedLanguage;
  toLang: SupportedLanguage;
  timestamp: number;
  expiresAt: number;
}

// AI Provider type
type AIProvider = 'deepseek';

export class AITranslationService {
  // Use Supabase Edge Function for secure AI API calls
  private static readonly AI_PROVIDER: AIProvider = 'deepseek';
  private static readonly EDGE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-openrouter-proxy`;
  private static readonly timeout = 30000;

  // Cache configuration
  private static readonly CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  private static readonly MAX_CACHE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly CACHE_KEY_PREFIX = 'translation_cache_';

  // Retry configuration
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY_MS = 1000; // 1 second
  private static readonly RETRY_BACKOFF_MULTIPLIER = 2;

  /**
   * Translate text from one language to another
   * @param text - Text to translate
   * @param fromLang - Source language
   * @param toLang - Target language
   * @param context - Translation context (survey, task, general)
   * @returns Translation result
   */
  static async translate(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage,
    context: TranslationContext = 'general'
  ): Promise<TranslationResult> {
    // Validate input
    if (!text || text.trim().length === 0) {
      throw new Error('Text to translate cannot be empty');
    }

    if (text.length > 10000) {
      throw new Error('Text too long for translation (max 10,000 characters)');
    }

    if (fromLang === toLang) {
      // No translation needed
      return {
        originalText: text,
        translatedText: text,
        fromLanguage: fromLang,
        toLanguage: toLang,
        cached: false,
        translatedAt: new Date()
      };
    }

    // Check cache first
    const cachedTranslation = this.getCachedTranslation(text, fromLang, toLang);
    if (cachedTranslation) {
      return {
        originalText: text,
        translatedText: cachedTranslation,
        fromLanguage: fromLang,
        toLanguage: toLang,
        cached: true,
        translatedAt: new Date()
      };
    }

    // Perform AI translation with retry
    try {
      const translatedText = await this.performAITranslationWithRetry(text, fromLang, toLang, context);

      // Cache the translation
      await this.cacheTranslation(text, translatedText, fromLang, toLang);

      return {
        originalText: text,
        translatedText,
        fromLanguage: fromLang,
        toLanguage: toLang,
        cached: false,
        translatedAt: new Date()
      };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      
      // Use error handler
      TranslationErrorHandler.handleAITranslationError(text, fromLang, toLang, errorObj);
      
      AIMonitoringService.logFailure({
        context: 'AITranslationService.translate',
        errorType: 'TRANSLATION_FAILED',
        error: errorObj.message,
        timestamp: new Date().toISOString(),
        stack: errorObj.stack
      });

      // Return original text instead of throwing
      return {
        originalText: text,
        translatedText: text,
        fromLanguage: fromLang,
        toLanguage: toLang,
        cached: false,
        translatedAt: new Date()
      };
    }
  }

  /**
   * Batch translate multiple texts
   * @param texts - Array of texts to translate
   * @param fromLang - Source language
   * @param toLang - Target language
   * @returns Array of translation results
   */
  static async batchTranslate(
    texts: string[],
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage
  ): Promise<TranslationResult[]> {
    const results: TranslationResult[] = [];

    for (const text of texts) {
      try {
        const result = await this.translate(text, fromLang, toLang);
        results.push(result);
      } catch (error) {
        console.error(`Failed to translate text: ${text.substring(0, 50)}...`, error);
        // Add failed translation with original text
        results.push({
          originalText: text,
          translatedText: text,
          fromLanguage: fromLang,
          toLanguage: toLang,
          cached: false,
          translatedAt: new Date()
        });
      }
    }

    return results;
  }

  /**
   * Get cached translation if available and not expired
   * @param text - Original text
   * @param fromLang - Source language
   * @param toLang - Target language
   * @returns Cached translation or null
   */
  static getCachedTranslation(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage
  ): string | null {
    try {
      const cacheKey = this.generateCacheKey(text, fromLang, toLang);
      const cachedData = localStorage.getItem(cacheKey);

      if (!cachedData) {
        return null;
      }

      const cacheEntry: TranslationCacheEntry = JSON.parse(cachedData);

      // Check if cache is expired
      if (Date.now() > cacheEntry.expiresAt) {
        // Remove expired cache
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheEntry.translatedText;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      TranslationErrorHandler.handleCacheError('getCachedTranslation', errorObj);
      return null;
    }
  }

  /**
   * Perform AI translation with retry mechanism
   * @param text - Text to translate
   * @param fromLang - Source language
   * @param toLang - Target language
   * @param context - Translation context
   * @returns Translated text
   */
  private static async performAITranslationWithRetry(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage,
    context: TranslationContext
  ): Promise<string> {
    let lastError: Error | null = null;
    let delay = this.RETRY_DELAY_MS;

    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        return await this.performAITranslation(text, fromLang, toLang, context);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Check if error is retryable
        const isRetryable = this.isRetryableError(lastError);
        
        if (!isRetryable || attempt === this.MAX_RETRIES) {
          // Don't retry or max retries reached
          throw lastError;
        }

        // Log retry attempt
        console.warn(`Translation attempt ${attempt} failed, retrying in ${delay}ms...`, lastError.message);

        // Wait before retrying
        await this.sleep(delay);
        
        // Exponential backoff
        delay *= this.RETRY_BACKOFF_MULTIPLIER;
      }
    }

    // Should never reach here, but just in case
    throw lastError || new Error('Translation failed after retries');
  }

  /**
   * Perform AI translation using Edge Function
   * @param text - Text to translate
   * @param fromLang - Source language
   * @param toLang - Target language
   * @param context - Translation context
   * @returns Translated text
   */
  private static async performAITranslation(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage,
    context: TranslationContext
  ): Promise<string> {
    const prompt = this.createTranslationPrompt(text, fromLang, toLang, context);
    
    const systemMessage = 'You are a professional translator. Translate the given text accurately while preserving the original meaning, tone, and formatting.';
    const fullPrompt = `${systemMessage}\n\n${prompt}`;

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session. Please sign in.');
      }

      const requestBody = {
        provider: this.AI_PROVIDER,
        prompt: fullPrompt,
        operation: 'translate',
        temperature: 0.3, // Lower temperature for more consistent translations
        maxTokens: 2000
      };

      const response = await fetch(this.EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Translation API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error('Invalid response from Edge Function');
      }

      const translatedText = data.data.trim();

      if (!translatedText) {
        throw new Error('Empty translation returned from API');
      }

      // Remove any markdown code blocks if present
      return this.cleanTranslationOutput(translatedText);
    } catch (error) {
      // Network error
      const networkError = error instanceof Error ? error : new Error(String(error));
      TranslationErrorHandler.handleNetworkError(networkError);
      throw new Error(`Network error during translation: ${networkError.message}`);
    }
  }

  /**
   * Check if error is retryable
   * @param error - Error to check
   * @returns True if error is retryable
   */
  private static isRetryableError(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    // Network errors are retryable
    if (message.includes('network') || message.includes('fetch')) {
      return true;
    }

    // Timeout errors are retryable
    if (message.includes('timeout')) {
      return true;
    }

    // 5xx server errors are retryable
    if (message.includes('500') || message.includes('502') || 
        message.includes('503') || message.includes('504')) {
      return true;
    }

    // Rate limit errors are retryable
    if (message.includes('429') || message.includes('rate limit')) {
      return true;
    }

    // 4xx client errors are generally not retryable
    if (message.includes('400') || message.includes('401') || 
        message.includes('403') || message.includes('404')) {
      return false;
    }

    // Default to not retryable
    return false;
  }

  /**
   * Sleep for specified milliseconds
   * @param ms - Milliseconds to sleep
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create translation prompt based on context
   * @param text - Text to translate
   * @param fromLang - Source language
   * @param toLang - Target language
   * @param context - Translation context
   * @returns Translation prompt
   */
  private static createTranslationPrompt(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage,
    context: TranslationContext
  ): string {
    const langNames = {
      en: 'English',
      zh: 'Chinese (Simplified)'
    };

    const contextInstructions = {
      survey: 'This is a survey question or response. Maintain clarity and ensure the translation is easy to understand for survey participants.',
      task: 'This is a task description or instruction. Keep the translation clear and actionable.',
      general: 'Translate naturally while preserving the original meaning and tone.'
    };

    return `Translate the following text from ${langNames[fromLang]} to ${langNames[toLang]}.

Context: ${contextInstructions[context]}

Important:
- Preserve all formatting (line breaks, bullet points, etc.)
- Maintain the original tone and style
- Do not add explanations or notes
- Return ONLY the translated text

Text to translate:
${text}`;
  }

  /**
   * Clean translation output by removing markdown code blocks
   * @param text - Raw translation output
   * @returns Cleaned translation
   */
  private static cleanTranslationOutput(text: string): string {
    // Remove markdown code blocks if present
    let cleaned = text.replace(/```[\w]*\n?/g, '').trim();
    
    // Remove any leading/trailing quotes
    if ((cleaned.startsWith('"') && cleaned.endsWith('"')) ||
        (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
      cleaned = cleaned.slice(1, -1);
    }

    return cleaned;
  }

  /**
   * Cache translation in localStorage
   * @param originalText - Original text
   * @param translatedText - Translated text
   * @param fromLang - Source language
   * @param toLang - Target language
   */
  private static async cacheTranslation(
    originalText: string,
    translatedText: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage
  ): Promise<void> {
    try {
      // Check cache size and cleanup if needed
      await this.cleanupCacheIfNeeded();

      const cacheKey = this.generateCacheKey(originalText, fromLang, toLang);
      const cacheEntry: TranslationCacheEntry = {
        originalText,
        translatedText,
        fromLang,
        toLang,
        timestamp: Date.now(),
        expiresAt: Date.now() + this.CACHE_TTL
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      TranslationErrorHandler.handleCacheError('cacheTranslation', errorObj);
      // Don't throw - caching failure shouldn't break translation
    }
  }

  /**
   * Generate cache key from text and language pair
   * @param text - Text to hash
   * @param fromLang - Source language
   * @param toLang - Target language
   * @returns Cache key
   */
  private static generateCacheKey(
    text: string,
    fromLang: SupportedLanguage,
    toLang: SupportedLanguage
  ): string {
    const hash = this.simpleHash(text);
    return `${this.CACHE_KEY_PREFIX}${hash}_${fromLang}_${toLang}`;
  }

  /**
   * Simple hash function for cache keys
   * @param str - String to hash
   * @returns Hash string
   */
  private static simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get current cache size in bytes
   * @returns Cache size in bytes
   */
  private static getCacheSize(): number {
    let totalSize = 0;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += key.length + value.length;
        }
      }
    }

    return totalSize * 2; // Approximate size in bytes (UTF-16)
  }

  /**
   * Cleanup cache if size exceeds limit
   */
  private static async cleanupCacheIfNeeded(): Promise<void> {
    const currentSize = this.getCacheSize();

    if (currentSize > this.MAX_CACHE_SIZE) {
      await this.clearOldestCacheEntries();
    }

    // Also cleanup expired entries
    await this.clearExpiredCacheEntries();
  }

  /**
   * Clear oldest cache entries to free up space
   */
  private static async clearOldestCacheEntries(): Promise<void> {
    const cacheEntries: Array<{ key: string; timestamp: number }> = [];

    // Collect all cache entries with timestamps
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const entry: TranslationCacheEntry = JSON.parse(value);
            cacheEntries.push({ key, timestamp: entry.timestamp });
          } catch (error) {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        }
      }
    }

    // Sort by timestamp (oldest first)
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp);

    // Remove oldest 25% of entries
    const entriesToRemove = Math.ceil(cacheEntries.length * 0.25);
    for (let i = 0; i < entriesToRemove; i++) {
      localStorage.removeItem(cacheEntries[i].key);
    }
  }

  /**
   * Clear expired cache entries
   */
  private static async clearExpiredCacheEntries(): Promise<void> {
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const entry: TranslationCacheEntry = JSON.parse(value);
            if (now > entry.expiresAt) {
              keysToRemove.push(key);
            }
          } catch (error) {
            // Invalid entry, remove it
            keysToRemove.push(key);
          }
        }
      }
    }

    // Remove expired entries
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Clear all translation cache
   */
  static clearCache(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  static getCacheStats(): {
    totalEntries: number;
    totalSize: number;
    expiredEntries: number;
  } {
    let totalEntries = 0;
    let expiredEntries = 0;
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
        totalEntries++;
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const entry: TranslationCacheEntry = JSON.parse(value);
            if (now > entry.expiresAt) {
              expiredEntries++;
            }
          } catch (error) {
            expiredEntries++;
          }
        }
      }
    }

    return {
      totalEntries,
      totalSize: this.getCacheSize(),
      expiredEntries
    };
  }
}
